/**
 * @fileoverview Provide searchable shared selection over caller-owned options. The workspace owns the chosen value and any data fetching; the control owns transient search, keyboard admission, and overlay state. Search may use caller-supplied summary fields without fetching additional records. Selection emits a value/label envelope, unlike the direct string callback in `AppSelect.js`. `AppIcon.js` supplies common visual signifiers.
 */

import React, { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from "./AppIcon";

const baseInputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  paddingRight: '2rem',
  borderRadius: '6px',
  border: '1px solid var(--surface-border)',
  fontSize: '1rem',
  backgroundColor: 'var(--surface-section)',
  color: 'var(--text-color)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  position: 'relative',
  outline: 'none',
  appearance: 'none',
  display: 'flex',
  alignItems: 'center'
};

const borderlessInputStyle = {
  ...baseInputStyle,
  border: 'none',
  borderBottom: '1px solid transparent',
  borderRadius: '0',
  backgroundColor: 'transparent',
  padding: '0.6rem 2rem 0.6rem 0'
};

const chevronStyle = {
  position: 'absolute',
  right: '0.75rem',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  fontSize: '0.75rem',
  color: 'var(--text-color-secondary, #64748b)'
};

const panelStyle = {
  position: 'fixed',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 5000,
  maxHeight: '250px',
  overflowY: 'auto',
  padding: '0.25rem 0'
};

const optionStyle = {
  padding: '0.6rem 1rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: 'var(--text-color)',
  whiteSpace: 'nowrap'
};

/**
 * onChange receives {value, label}. optionLabel/optionValue adapt domain records to selection choices without changing their source ownership. The caller remains responsible for validating and persisting the choice.
 */
function AppDropdown({ id, value, options = [], onChange, placeholder, variant, optionLabel, optionValue, disabled, className, autoFocus = false, openOnAutoFocus = false, inputId, style, itemTemplate, valueTemplate, showClear, optionDisabled, filter, filterBy, filterPlaceholder = 'Search...', filterInputProps, emptyFilterMessage = 'No results', panelClassName, scrollHeight, onBlur, ...props }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });

  const normalizedOptions = options.map(

  (opt) => {
    const label = optionLabel ? opt[optionLabel] : opt.label ?? opt.name ?? String(opt);
    const optionVal = optionValue ? opt[optionValue] : opt.value ?? (optionLabel ? opt : opt.name ?? opt);
    return { label: String(label), value: optionVal, option: opt,
      disabled: typeof optionDisabled === 'function' ? optionDisabled(opt) : optionDisabled ? opt[optionDisabled] : opt.disabled };

  });

  const filtered = search ?
  normalizedOptions.filter((o) => (filterBy ? filterBy.split(',').map(key => o.option[key.trim()] ?? '').join(' ') : o.label).toLowerCase().includes(search.toLowerCase())) :
  normalizedOptions;

  const isSelected = option => option.value === value || (typeof value === 'object' && value !== null && JSON.stringify(option.value) === JSON.stringify(value));
  const selectedLabel = normalizedOptions.find(isSelected)?.label;

  useEffect(

  () => {
    if (!open) {
      setSearch('');
      setHighlightIndex(-1);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);

    const handleClickOutside =

    (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { clearTimeout(focusTimer); document.removeEventListener('mousedown', handleClickOutside); };
  }, [open]);

  useEffect(() => {
    if (!autoFocus || disabled) return;

    const frame = requestAnimationFrame(() => {
      triggerRef.current?.focus();
      if (openOnAutoFocus) {
        setOpen(true);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [autoFocus, disabled, openOnAutoFocus]);

  const select =

  (opt) => {
    if (opt.disabled) return;
    onChange?.({ value: opt.value, label: opt.label });
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown =

  (e) => {
    if (e.key === 'Escape') {setOpen(false);triggerRef.current?.focus();return;}
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && filtered[highlightIndex]) {
      e.preventDefault();
      select(filtered[highlightIndex]);
    }
  };

  const isBorderless = variant === 'borderless';
  const inputSt = isBorderless ? borderlessInputStyle : baseInputStyle;

  return (
    <div style={{ position: 'relative' }} className={className || ''}>
            <button
        ref={triggerRef}
        id={inputId || id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && highlightIndex >= 0 ? `${listId}-${highlightIndex}` : undefined}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        onKeyDown={(e) => {
          if (open) { handleKeyDown(e); return; }
          if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); setHighlightIndex(0); }
        }}
        type="button"
        style={{
          ...inputSt,
          ...style,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        onFocus={isBorderless ? (e) => {e.currentTarget.style.borderBottom = '1px solid var(--primary-color)';} : undefined}
        onBlur={isBorderless ? (e) => {if (!open) e.currentTarget.style.borderBottom = '1px solid transparent';} : undefined}>

                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(valueTemplate && value != null ? valueTemplate(value, { placeholder }) : selectedLabel) || <span style={{ color: 'var(--text-color-secondary, #94a3b8)' }}>{placeholder || ''}</span>}
                </span>
                <span style={chevronStyle}><AppIcon icon="pi pi-chevron-down" /></span>
            </button>
            {showClear && value != null && <button type="button" aria-label="Clear selection" disabled={disabled}
              style={{ position: 'absolute', right: '2rem', top: '50%', transform: 'translateY(-50%)', border: 0, background: 'transparent', color: 'inherit' }}
              onClick={() => onChange?.({ value: null })}><AppIcon icon="pi pi-times" /></button>}

            {open && typeof document !== 'undefined' && createPortal(
        <div
          role="listbox"
          className={panelClassName}
          id={listId}
          ref={panelRef}
          style={{ ...panelStyle, ...(scrollHeight ? { maxHeight: scrollHeight } : {}), top: panelPos.top, left: panelPos.left, minWidth: panelPos.width }}
          onKeyDown={handleKeyDown}>

                    {(filter || normalizedOptions.length > 6) &&
          <div style={{ padding: '0.5rem' }}>
                            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => {setSearch(e.target.value);setHighlightIndex(0);}}
              placeholder={filterPlaceholder}
              {...filterInputProps}
              style={{
                width: '100%', padding: '0.4rem 0.6rem', borderRadius: '4px',
                border: '1px solid var(--surface-border)', fontSize: '0.875rem',
                backgroundColor: 'var(--surface-section)', color: 'var(--text-color)',
                outline: 'none'
              }}
               />

                        </div>}

                    {filtered.map((opt, i) =>
          <div
            key={i}
            role="option"
            id={`${listId}-${i}`}
            aria-disabled={opt.disabled || undefined}
            aria-selected={isSelected(opt)}
            style={{
              ...optionStyle,
              backgroundColor: i === highlightIndex ?
              'var(--surface-hover, var(--surface-ground))' :
              isSelected(opt) ?
              'var(--surface-100, var(--surface-ground))' :
              'transparent',
              fontWeight: isSelected(opt) ? 600 : 400,
              opacity: opt.disabled ? .5 : 1
            }}
            onMouseEnter={() => setHighlightIndex(i)}
            onClick={() => select(opt)}>

                            {itemTemplate ? itemTemplate(opt.option) : opt.label}
                        </div>
          )}
                    {filtered.length === 0 &&
          <div style={{ ...optionStyle, color: 'var(--text-color-secondary)' }}>{emptyFilterMessage}</div>}

                </div>,
        document.body
      )}
        </div>);

}

export default AppDropdown;

export { AppDropdown as Dropdown };
